/*
 * Authors: mariushirjanu, Koen
 * Created: 10/03/2015
 * Upgraded: 03/01/2023
 */
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { StateService } from '@uirouter/angular';
import { Subscription } from 'rxjs';
import { OrderCatalogService } from './order-catalog.service';
import { OrderCatalogDataService } from './order-catalog-data.service';
import { STATE_ORDER_CATALOG_DETAIL, STATE_ORDER_CATALOG_OVERVIEW } from '../../../shared/app-states';
import { CatalogStateService } from './catalog-state.service';
import { GA1Service } from '../../../shared/ga1/ga1.service';
import { CatalogOverviewService } from './overview/catalog-overview.service';
import { GaEventService, IGAEvent } from '../../../shared/services/ga-event.service';
import { EVENT_BREADCRUMBS_CHANGED } from '../../../shared/app-events';
import { PriceCartService } from '../cart/price-cart.service';
import { IBreadcrumb } from '../../../shared/breadcrumbs/breadcrumb.service';
import { IOrderContext, ProcurementType } from '../entry/order-entry-data.service';
import { OrderEntryWebshopDataService } from '../entry/webshop/order-entry-webshop-data.service';
import * as _ from 'underscore';

@Component({
  selector: 'order-catalog',
  template: `
    <div class="catalog">
      <h2 *ngIf="loading"><activity-indicator></activity-indicator></h2>
      
      <div *ngIf="!error && !loading">
        <breadcrumbs [breadcrumbs]="breadcrumbs"></breadcrumbs>
        <ui-view></ui-view>
      </div>
      
      <div *ngIf="error" class="alert alert-danger">
        <strong>{{error}}</strong>
      </div>
    </div>
  `,
  styleUrls: ['order-catalog.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OrderCatalogComponent implements OnInit, OnDestroy {
  @Input() public currency:string;
  @Input() public billType:string;
  @Input() public workflowType:string;
  @Input() public webshopId:number;
  @Input() public countryId:number;
  @Input() public bpCompanyCountryId:number;
  @Input() public stock:boolean;
  @Input() public layout:string;
  @Input() public defaultSort:string;
  @Input() public stockOrderType:string;

  public error:string;
  public breadcrumbs:IBreadcrumb[];
  public loading:boolean;
  private eventHandler:Subscription;

  constructor(
    private stateService:StateService,
    private orderCatalogService:OrderCatalogService,
    private catalogStateService:CatalogStateService,
    private catalogOverviewService:CatalogOverviewService,
    private orderCatalogDataService:OrderCatalogDataService,
    private orderEntryWebshopDataService:OrderEntryWebshopDataService,
    private priceCartService:PriceCartService,
    private ga1Service:GA1Service,
    private eventService:GaEventService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.start();
    this.listen();
  }

  private start():void {
    this.orderCatalogDataService.start();
    this.priceCartService.updateHandler(this.orderCatalogService.updateCartHandler);
    this.getStateParams();

    if (_.isUndefined(this.orderCatalogService.hidePrices) && _.isUndefined(this.orderCatalogService.isLeasingWebshop)) {
      this.loadContext();
    }
  }

  private listen():void {
    // ask ga1 for current cart status
    this.ga1Service.askCurrentCart();

    this.eventHandler = this.eventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_BREADCRUMBS_CHANGED) {
        this.breadcrumbs = event.data;
      }
    });
  }

  private loadContext():void {
    this.loading = true;
    this.orderEntryWebshopDataService.shopContext(this.orderCatalogService.webshopId, this.orderCatalogService.countryId, this.orderCatalogService.companyCountryId, undefined)
      .then((context:IOrderContext) => {
        this.orderCatalogService.hidePrices = context?.hidePrices;
        this.orderCatalogService.isLeasingWebshop = context?.defaultProcurementType === ProcurementType.LEASE;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private getStateParams():void {
    this.orderCatalogService.autoSwitchLayout = false;

    this.catalogStateService.baseParams = this.stateService.params;
    this.catalogStateService.overviewState = STATE_ORDER_CATALOG_OVERVIEW;
    this.catalogStateService.detailState = STATE_ORDER_CATALOG_DETAIL;

    const missingParams:string[] = [];
    if (this.currency) {
      this.orderCatalogService.currency = this.currency;
      this.priceCartService.currency = this.currency;
    } else {
      missingParams.push('currency');
    }

    if (this.billType) {
      this.orderCatalogService.billType = this.billType;
    } else {
      missingParams.push('billType');
    }

    if (this.workflowType) {
      this.orderCatalogService.workflowType = this.workflowType;
    } else {
      missingParams.push('workflowType');
    }

    if (this.webshopId) {
      this.orderCatalogService.webshopId = this.webshopId;
    } else {
      missingParams.push('webshop');
    }

    if (this.countryId) {
      this.orderCatalogService.countryId = this.countryId;
    } else {
      missingParams.push('country');
    }

    this.orderCatalogService.companyCountryId = this.bpCompanyCountryId;
    this.orderCatalogService.showStock = !!this.stock;
    this.orderCatalogService.layout = this.layout;
    this.orderCatalogService.sort = this.defaultSort;
    this.orderCatalogService.stockOrderType = this.stockOrderType;

    if (missingParams.length) {
      this.error = `Missing parameters: ${missingParams.join(', ')}`;
    } else {
      this.orderCatalogService.start();
      this.orderCatalogDataService.categories(this.orderCatalogService.workflowType, this.orderCatalogService.stockOrderType);
    }
  }

  public ngOnDestroy():void {
    this.priceCartService.updateHandler(undefined);
    this.orderCatalogDataService.clear();
    this.orderCatalogService.clear();
    this.catalogStateService.clear();
    this.catalogOverviewService.clear();
    this.eventHandler?.unsubscribe();
  }
}